'use strict';

define('vb/private/types/utils/cryptoUtils',[], () => {
  /**
   * This class contains crypto-related utility methods.
   */
  class CryptoUtils {
    /**
     * Encodes a string to Base64 URL-safe format. This format differs from standard Base64
     * in that the trailing '=' characters are removed, '+' is replaced with '-', and '/' is
     * replaced with '_'. This avoids the need for URL encoding, which can make the resulting
     * string longer due to %xx character replacements.
     *
     * @param {String} value - The string to encode.
     * @returns {String} The URL-safe Base64 encoded string.
     */
    static base64UrlEncode(value) {
      let encoded = btoa(value);

      // remove trailing '='
      encoded = encoded.split('=')[0];

      // replace + with - and / with _
      encoded = encoded.replaceAll('+', '-');
      encoded = encoded.replaceAll('/', '_');

      return encoded;
    }

    /**
     * Generates a random string that can be used as a code verifier in the Proof Key for Code Exchange (PKCE) scheme.
     *
     * According to the RFC 7636 specification, a code verifier is a high-entropy cryptographic random string that is
     * used to correlate the authorization request to the token request in OAuth 2.0.
     *
     * @link https://datatracker.ietf.org/doc/html/rfc7636#section-4.1
     *
     * @param {Number} [length=32] - The number of random bytes to use. Default is 32 bytes.
     * @returns {String} The generated code verifier which is Base64 URL-safe encoded.
     */
    static generateCodeVerifier(length = 32) {
      const randomBytes = window.crypto.getRandomValues(new Uint8Array(length));
      const randomString = String.fromCharCode(...randomBytes);

      return this.base64UrlEncode(randomString);
    }

    /**
     * Generates an S256 code challenge from the given code verifier.
     *
     * Only S256 is supported so callers cannot generate or request a PKCE plain challenge.
     *
     * @link https://datatracker.ietf.org/doc/html/rfc7636#section-4.2
     *
     * @param {String} codeVerifier - The code verifier used to generate the code challenge.
     * @returns {Promise<String>} A promise that resolves to the generated code challenge.
     */
    static generateCodeChallenge(codeVerifier) {
      return Promise.resolve().then(() => crypto.subtle.digest('SHA-256', new TextEncoder().encode(codeVerifier))
        .then((hashBuffer) => {
          const codeChallenge = this.base64UrlEncode(String.fromCharCode(...new Uint8Array(hashBuffer)));
          return codeChallenge;
        }));
    }
  }

  return CryptoUtils;
});

